import { api } from "./api";

export interface DashboardStats {
  totalClientes: number;
  totalEmprestado: string;
  totalRecebido: string;
  totalAberto: string;
  totalInadimplente: string;
  emprestimosAtivos: number;
  parcelasHoje: Array<{
    id: string;
    emprestimoId: string;
    valor: string;
    dataVencimento: string;
  }>;
  parcelasVencidas: Array<{
    id: string;
    emprestimoId: string;
    valor: string;
    dataVencimento: string;
  }>;
}

export async function getDashboardStats(): Promise<DashboardStats> {
  const response = await api.get<DashboardStats>("/dashboard/stats");
  return response.data!;
}

/* ============================================================================
 * /dashboard/overview — payload da Home real (12 KPIs + widgets)
 * ==========================================================================*/

export interface KpiValue {
  // Percentuais derivados (adimplencia, inadimplencia, recuperacao,
  // renovacao) devolvem null quando nao ha base pra calcular (0 clientes,
  // 0 parcelas). Monetarios continuam sempre number.
  value: number | null;
  deltaPct: number | null;
}

export interface DashboardKpis {
  capitalNaRua: KpiValue;
  totalAReceber: KpiValue;
  recebidoNoMes: KpiValue;
  venceHoje: KpiValue;
  emAtraso: KpiValue;
  disponivel: KpiValue;
  clientesAtivos: KpiValue;
  ticketMedio: KpiValue;
  inadimplenciaPct: KpiValue;
  adimplenciaPct: KpiValue;
  recuperacaoPct: KpiValue;
  renovacaoPct: KpiValue;
  jurosAReceber: KpiValue;
}

export interface MetaDia {
  meta: number;
  recebido: number;
  faltaReceber: number;
}

export interface Chart30dPoint {
  date: string;
  previsto: number;
  recebido: number;
}

export type AlertaKey =
  | "vencidos_ontem"
  | "tres_dias_sem_pagar"
  | "renovar_hoje"
  | "recuperados_semana";

export interface Alerta {
  key: AlertaKey;
  count: number;
  valor: number;
}

export type AtrasoBucketLabel = "1d" | "2-3d" | "4-7d" | "8-15d" | "+15d";

export interface AtrasoBucket {
  label: AtrasoBucketLabel;
  valor: number;
  clientes: number;
  pctMaxBucket: number;
}

export type OportunidadeKey =
  | "renovacao"
  | "aumento_limite"
  | "inativos_recuperaveis";

export interface OportunidadeItem {
  key: OportunidadeKey;
  count: number;
  valor: number;
}

export interface Oportunidades {
  items: OportunidadeItem[];
  total: number;
}

export type SaudeLabel = "Ruim" | "Regular" | "Boa" | "Excelente" | "—";

export interface Saude {
  score: number | null;
  label: SaudeLabel;
  message: string;
}

export type MovimentacaoTipo =
  | "recebimento"
  | "acordo"
  | "emprestimo_criado";

export interface Movimentacao {
  id: string;
  tipo: MovimentacaoTipo;
  clienteNome: string;
  valor: number;
  timestamp: string;
}

export interface DashboardGeo {
  porUf: Record<string, number>;
  totalBrasil: number;
}

export interface DashboardOverview {
  kpis: DashboardKpis;
  metaDia: MetaDia;
  chart30d: Chart30dPoint[];
  alertas: Alerta[];
  atrasoBuckets: AtrasoBucket[];
  oportunidades: Oportunidades;
  saude: Saude;
  movimentacoes: Movimentacao[];
  geo: DashboardGeo;
}

export async function getDashboardOverview(): Promise<DashboardOverview> {
  const response = await api.get<DashboardOverview>("/dashboard/overview");
  return response.data!;
}

export interface GeoNamedCount {
  nome: string;
  count: number;
}

export async function getGeoCidades(uf: string): Promise<GeoNamedCount[]> {
  const response = await api.get<{ cidades: GeoNamedCount[] }>(
    `/dashboard/geo/cidades?uf=${encodeURIComponent(uf)}`
  );
  return response.data?.cidades ?? [];
}

export async function getGeoBairros(
  uf: string,
  cidade: string
): Promise<GeoNamedCount[]> {
  const response = await api.get<{ bairros: GeoNamedCount[] }>(
    `/dashboard/geo/bairros?uf=${encodeURIComponent(uf)}&cidade=${encodeURIComponent(cidade)}`
  );
  return response.data?.bairros ?? [];
}
